import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Loader2, Calendar, Repeat, ChevronDown, GripVertical, Trash2, Edit, Sun, Clock, History } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { toLocalISODate } from '../lib/formatUtils';

const repeatLabels = { none: 'No repeat', daily: 'Daily', monthly: 'Monthly', yearly: 'Yearly' };

export const TodoPage = () => {
  const confirm = useConfirmation();
  const [tab, setTab] = useState('today');
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quick add
  const [quickTitle, setQuickTitle] = useState('');
  const [showExpanded, setShowExpanded] = useState(false);
  const [quickDesc, setQuickDesc] = useState('');
  const [quickDate, setQuickDate] = useState('');
  const [quickRepeat, setQuickRepeat] = useState('none');
  const quickRef = useRef(null);

  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', title: '', description: '', task_date: '', repeat_type: 'none' });

  // Drag state
  const [dragId, setDragId] = useState(null);

  const fetchTodos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/todos?tab=${tab}`);
      if (res.data.success) setTodos(res.data.data);
    } catch {}
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const handleQuickAdd = async () => {
    const title = quickTitle.trim();
    if (!title) return;
    try {
      const payload = { title, description: quickDesc, task_date: quickDate || undefined, repeat_type: quickRepeat };
      await api.post('/todos', payload);
      setQuickTitle(''); setQuickDesc(''); setQuickDate(''); setQuickRepeat('none'); setShowExpanded(false);
      fetchTodos();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuickAdd(); }
  };

  const handleToggle = async (todo) => {
    try {
      const res = await api.put(`/todos/${todo.id}/toggle`);
      if (res.data.next_instance) toast.success(`Next ${repeatLabels[todo.repeat_type].toLowerCase()} instance created`);
      fetchTodos();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (todo) => {
    const ok = await confirm({ title: 'Delete To-Do', description: `Delete "${todo.title}"?` });
    if (!ok) return;
    try { await api.delete(`/todos/${todo.id}`); fetchTodos(); } catch { toast.error('Failed'); }
  };

  const handleEdit = (todo) => {
    setEditForm({ id: todo.id, title: todo.title, description: todo.description || '', task_date: todo.task_date, repeat_type: todo.repeat_type || 'none' });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) { toast.error('Title required'); return; }
    try {
      await api.put(`/todos/${editForm.id}`, { title: editForm.title, description: editForm.description, task_date: editForm.task_date, repeat_type: editForm.repeat_type });
      setEditDialog(false); fetchTodos();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Drag & Drop
  const handleDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const items = [...todos];
    const dragIdx = items.findIndex(t => t.id === dragId);
    const targetIdx = items.findIndex(t => t.id === targetId);
    if (dragIdx < 0 || targetIdx < 0) return;
    const [moved] = items.splice(dragIdx, 1);
    items.splice(targetIdx, 0, moved);
    setTodos(items);
    setDragId(null);
    // Persist
    try {
      await api.put('/todos/batch/reorder', { items: items.map((t, i) => ({ id: t.id, position_index: i })) });
    } catch {}
  };

  const today = toLocalISODate();
  const todayOverdue = todos.filter(t => !t.is_completed).length;
  const isPast = tab === 'past';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="todo-page">
      <div>
        <h1 className="text-2xl font-bold">To Do</h1>
        <p className="text-muted-foreground">Your personal task list</p>
      </div>

      {/* Quick Add */}
      <Card data-testid="quick-add-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                ref={quickRef}
                value={quickTitle}
                onChange={e => setQuickTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add a task... (press Enter)"
                className="border-0 bg-transparent focus-visible:ring-0 px-0 text-base"
                data-testid="quick-add-input"
              />
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setShowExpanded(!showExpanded)} data-testid="expand-quick-add">
                <ChevronDown className={`h-4 w-4 transition-transform ${showExpanded ? 'rotate-180' : ''}`} />
              </Button>
              <Button size="sm" onClick={handleQuickAdd} disabled={!quickTitle.trim()} data-testid="quick-add-btn">Add</Button>
            </div>
            {showExpanded && (
              <div className="space-y-3 pt-2 border-t border-border/30">
                <Textarea value={quickDesc} onChange={e => setQuickDesc(e.target.value)} placeholder="Description (optional)" rows={2} data-testid="quick-add-desc" />
                <div className="flex gap-3">
                  <div className="flex items-center gap-2 flex-1">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input type="date" value={quickDate} onChange={e => setQuickDate(e.target.value)} className="flex-1" data-testid="quick-add-date" />
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <Repeat className="h-4 w-4 text-muted-foreground" />
                    <Select value={quickRepeat} onValueChange={setQuickRepeat}>
                      <SelectTrigger className="flex-1" data-testid="quick-add-repeat"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No repeat</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="today" data-testid="tab-today"><Sun className="h-3.5 w-3.5 mr-1.5" />Today</TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming"><Clock className="h-3.5 w-3.5 mr-1.5" />Upcoming</TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past"><History className="h-3.5 w-3.5 mr-1.5" />Past</TabsTrigger>
        </TabsList>

        {['today', 'upcoming', 'past'].map(t => (
          <TabsContent key={t} value={t}>
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : todos.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">
                <p className="text-lg font-medium mb-1">
                  {t === 'today' ? 'All clear for today!' : t === 'upcoming' ? 'No upcoming tasks' : 'No completed past tasks'}
                </p>
                <p className="text-sm">{t === 'today' ? 'Add a task above to get started' : t === 'upcoming' ? 'Schedule tasks for future dates' : 'Completed tasks will appear here'}</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-1.5">
                {todos.map(todo => (
                  <div
                    key={todo.id}
                    draggable={!isPast}
                    onDragStart={e => handleDragStart(e, todo.id)}
                    onDragOver={handleDragOver}
                    onDrop={e => handleDrop(e, todo.id)}
                    className={`group flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      todo.is_completed ? 'bg-muted/10 border-border/30' :
                      todo.is_overdue ? 'bg-red-500/5 border-red-500/20' :
                      'bg-card border-border/50 hover:border-primary/20'
                    } ${dragId === todo.id ? 'opacity-50' : ''}`}
                    data-testid={`todo-${todo.id}`}
                  >
                    {!isPast && <GripVertical className="h-4 w-4 text-muted-foreground/30 mt-0.5 shrink-0 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" />}
                    <Checkbox
                      checked={todo.is_completed}
                      onCheckedChange={() => handleToggle(todo)}
                      className="mt-0.5 shrink-0"
                      data-testid={`todo-check-${todo.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${todo.is_completed ? 'line-through text-muted-foreground' : ''}`} data-testid={`todo-title-${todo.id}`}>
                        {todo.title}
                      </p>
                      {todo.description && <p className={`text-xs mt-0.5 ${todo.is_completed ? 'text-muted-foreground/50' : 'text-muted-foreground'}`}>{todo.description}</p>}
                      <div className="flex items-center gap-2 mt-1.5">
                        {todo.is_overdue && <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[10px]">Overdue</Badge>}
                        <span className="text-[10px] text-muted-foreground">{new Date(todo.task_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                        {todo.repeat_type && todo.repeat_type !== 'none' && (
                          <Badge variant="outline" className="text-[10px] gap-0.5"><Repeat className="h-2.5 w-2.5" />{repeatLabels[todo.repeat_type]}</Badge>
                        )}
                      </div>
                    </div>
                    {!isPast && (
                      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(todo)} data-testid={`todo-edit-${todo.id}`}><Edit className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(todo)} data-testid={`todo-del-${todo.id}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={editForm.title} onChange={e => setEditForm(f => ({...f, title: e.target.value}))} data-testid="edit-todo-title" /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))} rows={2} data-testid="edit-todo-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Task Date</Label><Input type="date" value={editForm.task_date} onChange={e => setEditForm(f => ({...f, task_date: e.target.value}))} data-testid="edit-todo-date" /></div>
              <div className="space-y-1"><Label>Repeat</Label>
                <Select value={editForm.repeat_type} onValueChange={v => setEditForm(f => ({...f, repeat_type: v}))}>
                  <SelectTrigger data-testid="edit-todo-repeat"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No repeat</SelectItem><SelectItem value="daily">Daily</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button><Button onClick={handleSaveEdit} data-testid="edit-todo-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
